import React, { useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import {
  AmenityAvailabilityReturn,
  AmenityReturn,
} from '../clients/rust-api-client';
import { memberReservationService } from '../clients/ApiClients';
import { Input } from '../Components/Catalyst/input';
import { Select } from '../Components/Catalyst/select';
import { Field, Label } from '../Components/Catalyst/fieldset';
import { Text } from '../Components/Catalyst/text';
import { ErrorAlert } from '../Components/Custom/ErrorAlert';
import { formatMoney } from '../utils/billingHelpers';
import {
  addDaysToDate,
  addMinutesLocal,
  arrivalUnavailableReason,
  formatClock,
  formatDuration,
  formatLocalDate,
  formatLocalTime,
  freeNightsFrom,
  isFreeAmount,
  minutesBetween,
  nightsLabel,
  outOfSeasonText,
  remainingOver,
  spotsLeftText,
  stayFee,
  stayTimes,
  todayIn,
} from './reservationFormat';
import { reservationErrorMessage } from './ruleMessages';

/** What the homeowner has picked: a time the server offered as free. */
export interface PickedTime {
  start: string;
  end: string;
  /** Shared capacity: the fewest spots left over the booking. */
  remaining: number | null;
  /** Overnight: how many nights. */
  nights: number | null;
  /** The price the grid quoted, before the server's preview confirms it. */
  feeHint: string | null;
}

interface Props {
  amenity: AmenityReturn;
  homeId: string;
  /** Bump to fetch the day again (after a conflict, say). */
  reloadKey?: number;
  /** The date to open on; today at the association by default. */
  initialDate?: string;
  /**
   * Moving a booking: leave it out of the grid, so its own slot reads as
   * free and a move that overlaps the current time (extending by an hour)
   * can be picked. The server honours it only for this household's own
   * booking.
   */
  excludeReservationId?: string;
  onChange: (picked: PickedTime | null) => void;
}

/** Nights fetched ahead of an arrival when the amenity sets no maximum. */
const DEFAULT_NIGHT_WINDOW = 14;

/**
 * Why a day has nothing to offer, when it has nothing to offer.
 * Past starts are dropped before this is asked.
 */
const emptyDayReason = (a: AmenityAvailabilityReturn, amenity: AmenityReturn): string => {
  const statuses = a.starts.map((s) => s.status);
  if (a.out_of_season) return outOfSeasonText(amenity, a.date);
  if (!a.bookable) return 'This amenity is not taking bookings.';
  if (a.hours_configured && a.open_windows.length === 0) return 'Closed this day.';
  if (statuses.length === 0) return 'No times left this day.';
  if (statuses.every((s) => s === 'too_far')) return "That's too far ahead to book yet.";
  if (statuses.every((s) => s === 'too_soon' || s === 'past')) {
    return "It's too late to book this day — this amenity needs more notice.";
  }
  if (statuses.every((s) => s === 'closed')) return 'Closed this day.';
  return 'Fully booked this day.';
};

/**
 * Choosing when (#643, #640 phase 2). Every answer comes from the
 * server's availability grid; this only lays it out:
 *
 * - `exclusive` and `shared_capacity`: a date, a start time, a length.
 *   Shared slots say how many spots are left, and a full one can't be
 *   picked.
 * - `overnight`: an arrival date and a number of nights, from the
 *   per-night grid (`?date=&to=`). Check-in and check-out are the
 *   amenity's fixed times.
 *
 * Used by the booking panel and by moving a booking, so both offer
 * exactly the same choices.
 */
const TimePicker: React.FC<Props> = ({
  amenity,
  homeId,
  reloadKey = 0,
  initialDate,
  excludeReservationId,
  onChange,
}) => {
  const overnight = amenity.booking_mode === 'overnight';
  const shared = amenity.booking_mode === 'shared_capacity';
  const today = todayIn(amenity.timezone);
  const lastDay = amenity.max_lead_days
    ? addDaysToDate(today, amenity.max_lead_days)
    : undefined;
  const minNights = Math.max(1, amenity.min_nights ?? 1);
  const maxNights = amenity.max_nights ?? DEFAULT_NIGHT_WINDOW;

  const [date, setDate] = useState<string>(initialDate ?? today);
  const [availability, setAvailability] =
    useState<AmenityAvailabilityReturn | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [start, setStart] = useState<string | null>(null);
  const [duration, setDuration] = useState<number>(amenity.min_duration_minutes);
  const [nights, setNights] = useState<number>(minNights);

  useEffect(() => {
    if (!date) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setStart(null);
    memberReservationService
      .getMyAmenityAvailability({
        homeId,
        amenityId: amenity.id,
        date,
        // A stay needs the nights after the arrival, too.
        ...(overnight ? { to: addDaysToDate(date, maxNights) } : {}),
        ...(excludeReservationId ? { excludeReservationId } : {}),
      })
      .then(({ data }) => {
        if (!cancelled) setAvailability(data);
      })
      .catch((err) => {
        if (!cancelled) {
          setAvailability(null);
          setError(reservationErrorMessage(err, "Couldn't load that day."));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [homeId, amenity.id, date, reloadKey, overnight, maxNights, excludeReservationId]);

  // -- slots ---------------------------------------------------------------

  const starts = useMemo(
    () => (availability?.starts ?? []).filter((s) => s.status !== 'past'),
    [availability]
  );
  const chosen = starts.find((s) => s.start_local === start);

  // Every length from the minimum up to the latest end the server says
  // this start can run to, in the amenity's increments.
  const durations = useMemo(() => {
    if (!chosen?.max_end_local) return [];
    const longest = minutesBetween(chosen.start_local, chosen.max_end_local);
    const step = Math.max(1, availability?.slot_increment_minutes ?? 30);
    const out: number[] = [];
    for (
      let d = amenity.min_duration_minutes;
      d <= Math.min(longest, amenity.max_duration_minutes);
      d += step
    ) {
      out.push(d);
    }
    return out;
  }, [chosen, availability, amenity]);

  useEffect(() => {
    if (durations.length && durations.indexOf(duration) === -1) {
      setDuration(durations[0]);
    }
  }, [durations, duration]);

  // -- nights --------------------------------------------------------------

  const nightGrid = useMemo(() => availability?.nights ?? [], [availability]);
  const freeNights = overnight ? freeNightsFrom(nightGrid, date, maxNights) : 0;
  const nightChoices = useMemo(() => {
    const out: number[] = [];
    for (let n = minNights; n <= Math.min(freeNights, maxNights); n += 1) out.push(n);
    return out;
  }, [minNights, freeNights, maxNights]);

  useEffect(() => {
    if (nightChoices.length && nightChoices.indexOf(nights) === -1) {
      setNights(nightChoices[0]);
    }
  }, [nightChoices, nights]);

  // -- what to tell the parent --------------------------------------------

  const picked: PickedTime | null = useMemo(() => {
    if (overnight) {
      if (nightChoices.indexOf(nights) === -1) return null;
      const times = stayTimes(amenity, date, nights);
      if (!times) return null;
      return {
        ...times,
        remaining: null,
        nights,
        feeHint: stayFee(nightGrid, date, nights),
      };
    }
    if (!chosen || !durations.length || durations.indexOf(duration) === -1) return null;
    const end = addMinutesLocal(chosen.start_local, duration);
    return {
      start: chosen.start_local,
      end,
      remaining: shared ? remainingOver(starts, chosen.start_local, end) : null,
      nights: null,
      feeHint: null,
    };
  }, [overnight, nightChoices, nights, amenity, date, nightGrid, chosen, durations, duration, shared, starts]);

  const pickedKey = picked ? `${picked.start}|${picked.end}|${picked.remaining}|${picked.feeHint}` : '';
  useEffect(() => {
    onChange(picked);
    // `picked` is rebuilt every render; its key is what changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickedKey]);

  // -------------------------------------------------------------------------

  if (overnight) {
    const arrivalNight = nightGrid.find((n) => n.date === date);
    const checkIn = amenity.check_in_minute;
    const checkOut = amenity.check_out_minute;
    return (
      <div className="space-y-5">
        <Field>
          <Label>Arrival</Label>
          <Input
            type="date"
            value={date}
            min={today}
            max={lastDay}
            onChange={(e) => setDate(e.target.value)}
          />
        </Field>

        {loading ? (
          <Text className="text-sm">Loading nights…</Text>
        ) : error ? (
          <ErrorAlert>{error}</ErrorAlert>
        ) : availability && freeNights < minNights ? (
          <Text className="text-sm">
            {freeNights === 0
              ? arrivalUnavailableReason(arrivalNight)
              : `Only ${nightsLabel(freeNights)} free from that day — stays are at least ${nightsLabel(minNights)}.`}
          </Text>
        ) : availability ? (
          <>
            <Field>
              <Label>Nights</Label>
              <Select value={String(nights)} onChange={(e) => setNights(Number(e.target.value))}>
                {nightChoices.map((n) => {
                  const fee = stayFee(nightGrid, date, n);
                  return (
                    <option key={n} value={n}>
                      {nightsLabel(n)} — leave {formatLocalDate(addDaysToDate(date, n), { withYear: false })}
                      {isFreeAmount(fee) ? '' : ` · ${formatMoney(fee)}`}
                    </option>
                  );
                })}
              </Select>
            </Field>
            {checkIn !== null && checkIn !== undefined && checkOut !== null && checkOut !== undefined && (
              <Text className="text-sm">
                Check in from {formatClock(checkIn)} on {formatLocalDate(date, { withYear: false })}; check
                out by {formatClock(checkOut)} on{' '}
                {formatLocalDate(addDaysToDate(date, nights), { withYear: false })}.
              </Text>
            )}
          </>
        ) : null}
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <Field>
        <Label>Date</Label>
        <Input
          type="date"
          value={date}
          min={today}
          max={lastDay}
          onChange={(e) => setDate(e.target.value)}
        />
      </Field>

      <div>
        <div className="text-base/6 font-medium text-zinc-950 sm:text-sm/6 dark:text-white">
          Start time
        </div>
        {loading ? (
          <Text className="mt-2 text-sm">Loading times…</Text>
        ) : error ? (
          <ErrorAlert className="mt-2">{error}</ErrorAlert>
        ) : availability && starts.some((s) => s.status === 'available') ? (
          <div className="mt-2 flex flex-wrap gap-2" role="radiogroup" aria-label="Start time">
            {starts.map((s) => {
              const open = s.status === 'available';
              const selected = s.start_local === start;
              const spots = shared ? spotsLeftText(s.remaining_capacity) : '';
              return (
                <button
                  key={s.start_local}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  disabled={!open}
                  aria-label={`${formatLocalTime(s.start_local)}${
                    open ? (spots ? `, ${spots}` : '') : s.status === 'full' ? ', full' : ', unavailable'
                  }`}
                  onClick={() => setStart(s.start_local)}
                  className={clsx(
                    'min-h-[2.75rem] rounded-lg px-3 text-sm font-medium ring-1 transition',
                    selected
                      ? 'bg-sky-700 text-white ring-sky-700'
                      : open
                        ? 'bg-white text-zinc-900 ring-zinc-300 hover:ring-sky-500 dark:bg-zinc-900 dark:text-white dark:ring-zinc-600'
                        : 'cursor-not-allowed bg-zinc-100 text-zinc-400 line-through ring-zinc-200 dark:bg-zinc-800 dark:text-zinc-500 dark:ring-zinc-700'
                  )}
                >
                  {formatLocalTime(s.start_local)}
                  {shared && open && s.remaining_capacity !== null && s.remaining_capacity !== undefined && (
                    <span
                      className={clsx(
                        'ml-1 text-xs font-normal',
                        selected ? 'text-sky-100' : 'text-zinc-500 dark:text-zinc-400'
                      )}
                    >
                      · {s.remaining_capacity} left
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        ) : availability ? (
          <Text className="mt-2 text-sm">{emptyDayReason({ ...availability, starts }, amenity)}</Text>
        ) : null}
      </div>

      {chosen && durations.length > 0 && (
        <Field>
          <Label>How long</Label>
          <Select value={String(duration)} onChange={(e) => setDuration(Number(e.target.value))}>
            {durations.map((d) => (
              <option key={d} value={d}>
                {formatDuration(d)} — until {formatLocalTime(addMinutesLocal(chosen.start_local, d))}
              </option>
            ))}
          </Select>
        </Field>
      )}

      {shared && picked && picked.remaining !== null && (
        <Text className="text-sm">{spotsLeftText(picked.remaining, amenity.capacity)} for that time.</Text>
      )}
    </div>
  );
};

export default TimePicker;
